/**
 * ZAdminOrgOAuthConnections - Zod Validation Schemas for Admin Org OAuth Connections API
 *
 * Admin endpoints for viewing OAuthConnection records per organization.
 * Sensitive fields (accessToken, refreshToken) are intentionally excluded.
 *
 * All endpoints are PBAC-protected (admin:organizations).
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS
// ═══════════════════════════════════════════════════════════════

/**
 * Single OAuth connection shape returned to the admin panel.
 * Dates are serialized to ISO strings. Tokens are excluded.
 */
export const AdminOrgOAuthConnectionSchema = z.object({
  id: z.string(),
  service: z.string(),
  status: z.string(),
  failureReason: z.string().nullable(),
  accountId: z.string(),
  accountEmail: z.string().nullable(),
  accountName: z.string().nullable(),
  /** ISO date string */
  expiresAt: z.string(),
  scope: z.array(z.string()),
  lastRefreshedAt: z.string().nullable(),
  webhookSubscriptionId: z.string().nullable(),
  webhookExpiresAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastUsedAt: z.string().nullable(),
  deploymentsCount: z.number().int()
});

export type AdminOrgOAuthConnection = z.infer<typeof AdminOrgOAuthConnectionSchema>;

// ═══════════════════════════════════════════════════════════════
// ZADMINORGOAUTHCONNECTIONS MANIFEST
// ═══════════════════════════════════════════════════════════════

export const ZAdminOrgOAuthConnections = {
  /**
   * List all OAuth connections for an organization
   * GET /api/admin/organizations/:orgId/oauth-connections
   */
  ListOAuthConnections: {
    method: "GET" as const,
    path: "/api/admin/organizations/:orgId/oauth-connections",
    PathVars: z.object({ orgId: z.string().min(1) }),
    Response: ApiSuccessSchema(z.object({ connections: z.array(AdminOrgOAuthConnectionSchema) }))
  }
} as const;

// ═══════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════

export type AdminOrgOAuthConnectionsListResponse = z.infer<
  typeof ZAdminOrgOAuthConnections.ListOAuthConnections.Response
>;
